/* eslint-disable i18next/no-literal-string */

import { Box, Button, Theme, Typography } from "@outschool/backpack";
import { SearchUseCase } from "@outschool/gql-frontend-generated";
import { BASE_LOCALE, useLocale } from "@outschool/localization";
import { searchPath, toOnlineClassesSlugID } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  Testimonial,
  TutorMatchEnrollmentDataContext,
  TutorMatchEnrollmentDataType
} from "@outschool/ui-components-website";
import {
  Loading,
  TrackingPage,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import { useNodeRef } from "@outschool/ui-utils";
import { TFunction, TOptions } from "i18next";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useDebouncedCallback as useDebouncedCallbackVendor } from "use-debounce";

import TestimonyJpg from "../../../../../images/school-page/testimony.jpg";
import * as Env from "../../../../../shared/Env";
import * as Routes from "../../../../../shared/Routes";
import { useSchoolPageQuery } from "../../../../hooks/useSchoolPageQuery";
import useTrackSearchFiltersApplied from "../../../../hooks/useTrackSearchFiltersApplied";
import TutorMatchActivityEnrollPage from "../../../../routes/LearningPlanner/enrollmentFlow/TutorMatchActivityEnrollPage";
import NotFoundPage from "../../../../routes/NotFoundPage";
import { EnrollmentFromStripePurchaseOrder } from "../../../enrolling/EnrollmentMeetingPurchaseWithStripe";
import HeaderTags from "../../../HeaderTags";
import SimpleLayout from "../../../layouts/SimpleLayout";
import Link from "../../../Link";
import CuratedWhatIsOutschool from "../../curated/CuratedWhatIsOutschool";
import { useListingsData } from "../../hooks/useListingsData";
import CategoryListings from "../CategoryListings";
import JumpPillsCarousel from "../JumpPillsCarousel";
import {
  ImageCard,
  PopularClassTopics,
  SchoolPageHero,
  StyledNavLink
} from "./components";
import {
  getFormattedGradesString,
  getTranslatedGradeCards,
  getTranslatedPills,
  getTranslatedValuePropCards
} from "./utils";

export default function SchoolPage() {
  const locale = useLocale();
  const history = useHistory();
  const trackEvent = useTrackEvent();
  const { searchUid, trackSearchFiltersApplied } =
    useTrackSearchFiltersApplied();
  const [tutorMatchEnrollmentData, setTutorMatchEnrollmentData] = useState<
    TutorMatchEnrollmentDataType | undefined
  >(undefined);

  /**
   * Stub for the i18n translation function. This code was built using
   * the actual function, but it was decided that the first version of the page
   * should be English only. The plan is to support other languages in the near
   * future, so we stubbed the function instead of removing it entirely.
   */
  const tStub: TFunction = (key: string, options?: TOptions | string) => {
    if (!options) {
      return key;
    }
    return key.replace(/{{(.*?)}}/g, (match, key) => {
      const trimmedKey = key.trim();

      if (typeof options === "object" && trimmedKey in options) {
        return options[trimmedKey];
      }
      return match;
    });
  };
  const [tutorMatchActivityEnrollNode, tutorMatchActivityEnrollRef] =
    useNodeRef();
  const { trackTouch: trackTutorMatchEnroll } = useImpressionTracking({
    node: tutorMatchActivityEnrollNode,
    trackingLabel: "school_page_activity_enroll",
    sharedProperties: {
      activity_uid: tutorMatchEnrollmentData?.activity.uid,
      sectionUid: tutorMatchEnrollmentData?.section?.uid
    },
    uniqueId: `school_page_activity_enroll-${tutorMatchEnrollmentData?.activity.uid}`
  });

  const [tutorMatchActivityEnrollmentOnComplete] = useDebouncedCallbackVendor(
    async (enrollments: EnrollmentFromStripePurchaseOrder[]) => {
      if (tutorMatchEnrollmentData) {
        const enrollment = enrollments[0];
        if (enrollment) {
          // track learner planner enrollment
          trackTutorMatchEnroll({
            enrollmentUid: enrollment.uid,
            activityUid: tutorMatchEnrollmentData.activity.uid,
            sectionUid: enrollment.section_uid
          });
        }

        enrollment &&
          setTutorMatchEnrollmentData?.({
            ...tutorMatchEnrollmentData,
            section: { uid: enrollment?.section_uid! }
          });

        return Promise.resolve();
      }
    },
    500
  );

  const schoolPageSlug =
    toOnlineClassesSlugID(
      useParams<{ themeOrQuery?: string }>()?.themeOrQuery || ""
    ) || "";

  const {
    loading: loadingSchoolPage,
    data
    // error
  } = useSchoolPageQuery(schoolPageSlug, { fetchPolicy: "network-only" });
  const schoolPage = data?.schoolPage;

  const {
    error: listingError,
    exactTotal,
    fetchMore,
    isFetchingMore,
    listings,
    loading: loadingListings
  } = useListingsData({
    categorySlug: schoolPageSlug,
    skip: loadingSchoolPage,
    predefinedCategoryFilters: schoolPage?.searchFilters || {},
    trackSearchFiltersApplied,
    pageLocation: "BASIC_SCHOOL",
    usePromotedRanking: true,
    useCase: SearchUseCase.CategoryContent
  });

  const metatags = React.useMemo(() => {
    return [
      {
        property: "og:title",
        content: `${schoolPage?.name} Parent Resources | Enrichment & Tutoring for ${schoolPage?.city}, ${schoolPage?.state}`
      },
      {
        name: "description",
        content: `Discover tutoring, enrichment classes, and resources for ${schoolPage?.name} parents. Access curated classes on Outschool to support your child’s learning journey in ${schoolPage?.state}.`
      },
      {
        property: "og:description",
        content: `Discover tutoring, enrichment classes, and resources for ${schoolPage?.name} parents. Access curated classes on Outschool to support your child’s learning journey in ${schoolPage?.state}.`
      },
      { name: "robots", content: "noindex" }
    ];
  }, [schoolPage]);

  useEffect(() => {
    if (!loadingSchoolPage && schoolPage === null) {
      trackEvent("school page client redirect to search", { schoolPageSlug });
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      history.replace(searchPath({ q: schoolPageSlug.replace(/-/g, " ") }));
    }
  }, [loadingSchoolPage, schoolPage, schoolPageSlug, trackEvent, history]);

  // Do not show SLPs for non-English locales
  // Reason: SLPs are for US public schools so they are English-only pages
  if (locale != BASE_LOCALE) {
    return <NotFoundPage />;
  } else if (!schoolPage) {
    return <Loading fullHeight={true} />;
  }

  const formattedGradesString = getFormattedGradesString(
    tStub,
    schoolPage?.startGrade,
    schoolPage?.endGrade
  );
  const gradeCardsData = getTranslatedGradeCards(tStub);
  const pillsData = getTranslatedPills(tStub);
  const valuePropCardsData = getTranslatedValuePropCards(tStub);

  return (
    <>
      <HeaderTags
        // @ts-ignore TS(2532): Object is possibly 'undefined'.
        title={`${schoolPage?.name} Parent Resources | Enrichment & Tutoring for ${schoolPage?.city}, ${schoolPage?.state}`}
        // @ts-ignore TS(2322): Type '({ property: string; content: Maybe<string> ... Remove this comment to see the full error message
        meta={metatags}
        canonical={Routes.categoryPageUrl(
          // @ts-ignore TS(2532): Object is possibly 'undefined'.
          schoolPage?.slug,
          schoolPage?.basePath
        )}
      />
      <TrackingPage
        impressionEventName="School Landing Page Impression"
        uniqueId="schoolLandingPageImpression"
        name="schoolLandingPageImpression"
        useLinkTracking
      >
        {setImpressionNode => (
          <SimpleLayout
            ref={node => {
              setImpressionNode(node!);
            }}
          >
            <TutorMatchEnrollmentDataContext.Provider
              value={{ tutorMatchEnrollmentData, setTutorMatchEnrollmentData }}
            >
              <SchoolPageHero
                name={schoolPage?.name || ""}
                city={schoolPage?.city || ""}
                state={schoolPage?.state || ""}
                address={schoolPage?.address || ""}
                tStub={tStub}
              />
              <Box flex sx={{ justifyContent: "center" }}>
                <Box
                  flex
                  sx={{
                    marginLeft: "1em",
                    marginRight: "1em",
                    marginBottom: "6em",
                    maxWidth: 1120,
                    flexDirection: "column",
                    width: "100%"
                  }}
                >
                  <Box
                    flex
                    sx={{
                      justifyContent: "center",
                      marginTop: "2em",
                      gap: "1em",
                      flexWrap: "wrap",
                      textAlign: "center",
                      flexShrink: 1
                    }}
                  >
                    {valuePropCardsData.map(card => (
                      <Box
                        key={card.text}
                        sx={(theme: Theme) => ({
                          [theme.breakpoints.down("md")]: {
                            maxWidth: "45%"
                          }
                        })}
                      >
                        <ImageCard
                          text={card.text}
                          imgUrl={card.imgUrl}
                          imgAspectRatioHeight={144}
                          imgAspectRatioWidth={144}
                          cardWidth={260}
                          hideBorder
                        />
                      </Box>
                    ))}
                  </Box>
                  <Box
                    flex
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      marginTop: "6em",
                      marginBottom: "6em"
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{ paddingLeft: "1em", paddingRight: "1em" }}
                    >
                      {formattedGradesString}
                    </Typography>
                    <Box
                      flex
                      sx={{
                        justifyContent: "center",
                        marginTop: "2em",
                        gap: "1em",
                        flexWrap: "wrap"
                      }}
                    >
                      {gradeCardsData.map(card => (
                        <StyledNavLink
                          to={card.link}
                          key={card.link}
                          sx={(theme: Theme) => ({
                            [theme.breakpoints.down("md")]: {
                              maxWidth: "45%"
                            }
                          })}
                        >
                          <ImageCard
                            text={card.text}
                            imgUrl={card.imgUrl}
                            imgAspectRatioHeight={180}
                            imgAspectRatioWidth={265}
                            cardWidth={265}
                          />
                        </StyledNavLink>
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ marginBottom: "6em" }}>
                    <JumpPillsCarousel
                      pillsData={pillsData}
                      title={tStub("Tutors For Any Topic")}
                      containerStyle={{
                        margin: 0,
                        padding: 0,
                        maxWidth: "none"
                      }}
                      titleVariant="h2"
                    />

                    <CategoryListings
                      isReadOnlyMode={Env.IS_READ_ONLY_MODE}
                      categorySlug={schoolPageSlug!}
                      error={listingError}
                      exactTotal={exactTotal}
                      fetchMore={fetchMore}
                      isFetchingMore={isFetchingMore}
                      listings={listings}
                      loading={loadingSchoolPage || loadingListings}
                      searchUid={searchUid}
                      variant={"category-basic"}
                      pageLocation="BASIC_SCHOOL"
                      isTutoringCLP={true}
                      hideLoadMoreButton
                    />
                    <Box
                      flex
                      sx={{ justifyContent: "center", marginTop: "1em" }}
                    >
                      <Button component={Link} to={"/search"}>
                        {tStub("Show More")}
                      </Button>
                    </Box>
                  </Box>
                  <Box
                    sx={(theme: Theme) => ({
                      marginBottom: "4em",
                      [theme.breakpoints.down("md")]: {
                        padding: "1em"
                      }
                    })}
                  >
                    <CuratedWhatIsOutschool isTutoringCLP={false} />
                  </Box>
                  <StyledNavLink to="/classes/k-12-11-math-tutoring-one-on-one-private-session-yrZplngj">
                    <Testimonial
                      name={tStub("Meagan K-12 1:1 Math Tutoring")}
                      text={tStub(
                        "Mr Ilya is the absolute best tutor ever!! My daughter was failing math with a 40% on a previous test he helped my daughter study for a unit review and she got a 94%. We couldn't be happier and he has lots of available time slots that help so much!!"
                      )}
                      image={TestimonyJpg}
                    />
                  </StyledNavLink>
                  <Box sx={{ marginTop: "6em" }}>
                    <PopularClassTopics tStub={tStub} titleVariant="h2" />
                  </Box>
                </Box>
              </Box>
              {tutorMatchEnrollmentData && (
                <TutorMatchActivityEnrollPage
                  ref={tutorMatchActivityEnrollRef}
                  tutorMatchEnrollmentData={tutorMatchEnrollmentData}
                  onCancel={() => {
                    setTutorMatchEnrollmentData(undefined);
                  }}
                  onPaymentComplete={tutorMatchActivityEnrollmentOnComplete}
                />
              )}
            </TutorMatchEnrollmentDataContext.Provider>
          </SimpleLayout>
        )}
      </TrackingPage>
    </>
  );
}
